import * as React from 'react';
import { Bug, LoaderCircle } from 'lucide-react';
import {
  DashboardDataTable,
  DashboardDataTableColumnKind,
  DashboardTableDateCell,
  DashboardTableMultiSelectFilter,
  DashboardTableSearchInput,
  DashboardTableStatusBadge,
  DashboardTableStatusTone,
  buildDashboardDataTableColumns,
} from '@shigo-ui/dashboard-ui';
import { Button, Tooltip, TooltipContent, TooltipTrigger } from '@shigo-ui/ui';

import { showRequestError } from '../../../../../lib/requestErrorToast';
import { useDebouncedValue } from '../../../../../hooks/useDebouncedValue';
import { dashboardT } from '../../../../../lib/messages';
import { ShigoPermission } from '../../../../auth/permissions';
import { useShigoPermissions } from '../../../../auth/useShigoPermissions';
import { useAdsTxtDomainCountsQuery, useAdsTxtDomainsQuery, useAdsTxtMutations } from '../../hooks';
import {
  adsTxtPageSizes,
  AdsTxtDomainStatus,
  AdsTxtEntityStatus,
  formatAdsTxtDomainResult,
  hasAdsTxtCrawlDateChanged,
  type AdsTxtDomain,
} from '../../model';
import { useAdsTxtDomainsRouteFilters } from '../../useAdsTxtDomainsRouteFilters';
import { formatDisplayDate } from '../../../../../lib/format';

interface CrawlTarget {
  key: string;
  url: string;
  previousDate: string | null | undefined;
}

export function AdsTxtDomainsTable() {
  const { can } = useShigoPermissions();
  const routeFilters = useAdsTxtDomainsRouteFilters();
  const { filter } = routeFilters;
  const query = useAdsTxtDomainsQuery(filter);
  const { refetch } = query;
  const counts = useAdsTxtDomainCountsQuery();
  const mutations = useAdsTxtMutations();
  const search = useDebouncedValue(filter.searchTerm, routeFilters.setSearch, 650);
  const [crawlTarget, setCrawlTarget] = React.useState<CrawlTarget | null>(null);

  React.useEffect(() => {
    if (!crawlTarget) return;
    let attempts = 0;
    const timer = window.setInterval(() => {
      void refetch().then((result) => {
        attempts += 1;
        const completed = result.data?.content.some(
          (domain) =>
            domain.url === crawlTarget.url &&
            hasAdsTxtCrawlDateChanged(crawlTarget.previousDate, domain.adstxtCrawledOn),
        );
        if (completed || attempts >= 30) setCrawlTarget(null);
      });
    }, 2_000);
    return () => {
      window.clearInterval(timer);
    };
  }, [crawlTarget, refetch]);

  const columns = buildDashboardDataTableColumns<AdsTxtDomain>([
    textColumn('name', 'adsTxt.domains.domain', (domain) => domain.url || '—', 'w-72'),
    {
      id: 'status',
      kind: DashboardDataTableColumnKind.Custom,
      header: dashboardT('adsTxt.domains.status'),
      headerClassName: 'w-32',
      className: 'w-32',
      cell: (domain) => (
        <DashboardTableStatusBadge
          tone={
            domain.status === AdsTxtEntityStatus.Active
              ? DashboardTableStatusTone.Success
              : DashboardTableStatusTone.Neutral
          }
        >
          {dashboardT(`adsTxt.entityStatus.${domain.status}`)}
        </DashboardTableStatusBadge>
      ),
    },
    textColumn(
      'publisher',
      'adsTxt.domains.publisher',
      (domain) => domain.publisherName || '—',
      'w-56',
    ),
    {
      id: 'result',
      kind: DashboardDataTableColumnKind.Custom,
      header: dashboardT('adsTxt.domains.result'),
      headerClassName: 'w-28 text-center',
      className: 'w-28 text-center font-medium tabular-nums',
      cell: (domain) => <AdsTxtDomainResult domain={domain} />,
    },
    {
      id: 'crawlDate',
      kind: DashboardDataTableColumnKind.Custom,
      header: dashboardT('adsTxt.crawlDate'),
      headerClassName: 'w-44',
      className: 'w-44',
      cell: (domain) => (
        <DashboardTableDateCell>
          {formatDisplayDate(domain.adstxtCrawledOn, {
            fallback: '—',
            keepInvalid: true,
            month: '2-digit',
            day: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          })}
        </DashboardTableDateCell>
      ),
    },
    {
      id: 'crawl',
      kind: DashboardDataTableColumnKind.Actions,
      header: dashboardT('adsTxt.crawl'),
      headerClassName: 'w-20 text-center',
      className: 'w-20 text-center',
      cell: (domain) =>
        can(ShigoPermission.SupplyAdsTxtDomainsCrawl) ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  disabled={!domain.url || Boolean(crawlTarget)}
                  aria-label={dashboardT('adsTxt.domains.crawlDomain', { domain: domain.url })}
                  onClick={() => void startCrawl(domain)}
                >
                  {crawlTarget?.key === domainKey(domain) ? (
                    <LoaderCircle className="h-4 w-4 animate-spin" />
                  ) : (
                    <Bug className="h-4 w-4" />
                  )}
                </Button>
              </span>
            </TooltipTrigger>
            {!domain.url ? (
              <TooltipContent>{dashboardT('adsTxt.domains.missingUrl')}</TooltipContent>
            ) : null}
          </Tooltip>
        ) : null,
    },
  ]);

  async function startCrawl(domain: AdsTxtDomain) {
    if (!domain.url) return;
    setCrawlTarget({
      key: domainKey(domain),
      url: domain.url,
      previousDate: domain.adstxtCrawledOn,
    });
    try {
      await mutations.crawlDomain.mutateAsync({
        publisherId: domain.publisherId,
        domainId: domain.domainId,
      });
    } catch (error) {
      setCrawlTarget(null);
      showRequestError(error, dashboardT('adsTxt.domains.crawlError'));
    }
  }

  return (
    <main className="page-gutter min-w-0 flex-1 py-4 md:py-6">
      <DashboardDataTable
        title={dashboardT('adsTxt.domains.title')}
        tableLabel={dashboardT('adsTxt.domains.title')}
        columns={columns}
        items={query.data?.content ?? []}
        getRowKey={domainKey}
        loading={query.isLoading}
        loadingMessage={dashboardT('adsTxt.domains.loading')}
        errorMessage={query.isError ? dashboardT('adsTxt.domains.loadError') : undefined}
        emptyMessage={dashboardT('adsTxt.domains.empty')}
        minWidthClassName="min-w-[980px]"
        toolbar={
          <>
            <DashboardTableSearchInput
              aria-label={dashboardT('adsTxt.domains.search')}
              placeholder={dashboardT('adsTxt.domains.search')}
              value={search.draft}
              onChange={(event) => {
                search.setDraft(event.target.value);
              }}
            />
            <DashboardTableMultiSelectFilter
              ariaLabel={dashboardT('adsTxt.domains.statusFilter')}
              label={
                filter.statuses.length
                  ? filter.statuses.map(statusLabel).join(', ')
                  : dashboardT('adsTxt.domains.allStatuses')
              }
              value={filter.statuses}
              options={Object.values(AdsTxtDomainStatus).map((status) => ({
                value: status,
                label: statusLabel(status),
                count: counts.data?.[status] ?? 0,
              }))}
              onChange={routeFilters.setStatuses}
            />
          </>
        }
        pagination={{
          value: {
            page: filter.page,
            pageSize: filter.size,
            totalItems: query.data?.totalElements ?? 0,
          },
          pageSizes: adsTxtPageSizes,
          disabled: query.isFetching,
          onChange: (next) => {
            routeFilters.setPagination(next.page, next.pageSize);
          },
        }}
      />
    </main>
  );
}

export function AdsTxtDomainResult({ domain }: { domain: AdsTxtDomain }) {
  return (
    <span className={domain.adstxtTotal == null ? 'text-muted-foreground' : undefined}>
      {formatAdsTxtDomainResult(domain.adstxtFound ?? null, domain.adstxtTotal ?? null)}
    </span>
  );
}

function domainKey(domain: AdsTxtDomain) {
  return `${domain.publisherId}:${domain.domainId}`;
}
function statusLabel(status: AdsTxtDomainStatus) {
  return dashboardT(`adsTxt.domainStatus.${status}`);
}
function textColumn(
  id: string,
  key: Parameters<typeof dashboardT>[0],
  value: (domain: AdsTxtDomain) => React.ReactNode,
  width: string,
) {
  return {
    id,
    kind: DashboardDataTableColumnKind.Text as const,
    header: dashboardT(key),
    headerClassName: width,
    className: width,
    value,
  };
}
